// @ts-nocheck
import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import moment from 'moment'
import MiniSwitch from '../../formUtils/MiniSwitch'
import EcoModeForm from './EcoModeForm'
import CustomConfirmationModal from '../../CustomConfirmationModal'
import { useApi } from '../../../hooks/useApi'

interface EcoMode {
  _id: string
  smartVault: string
  active: boolean
  averageSuccessSpeedSmoothFactor: number
  safeGuardPeriodPct: number
  maximumExecutionsPerPeriod: number
  gasPriceMedianThreshold: number
  isolatedTasksAvoidanceList: string[]
  updatedAt: string
}

const EcoModes: React.FC<{ chainId: number; smartVaults: any }> = ({ chainId, smartVaults }) => {
  const params = useParams<{ id: string }>()
  const { callApi } = useApi()
  const [ecoModes, setEcoModes] = useState(null)
  const [form, setForm] = useState(false)
  const [ecoModalOpen, setEcoModalOpen] = useState(false)
  const [deleteEcoMode, setDeleteEcoMode] = useState<EcoMode | null>(null)
  const [timelocks, setTimelocks] = useState(null)
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [editItem, setEditItem] = useState<EcoMode | null>(null)
  const [editedProps, setEditedProps] = useState<EcoMode | null>(null)

  const [smartVaultInfo] = useState(smartVaults.find((sv: any) => sv.chainId === chainId))
  const deniedTasks = smartVaultInfo?.deniedTasks

  const fetchEcoModes = async () => {
    try {
      const ecos = await callApi(`/relayer-executor/environments/${params.id}/eco-modes`, { chainId })
      setEcoModes(ecos)
    } catch (error) {
      console.error('Error fetching eco modes:', error)
    }
  }

  const fetchTimelocks = async () => {
    try {
      const data = await callApi(`/relayer-executor/smart-vaults/${smartVaultInfo._id}/timelocks`)
      setTimelocks(data)
    } catch (error) {
      console.error('Error fetching timelocks:', error)
    }
  }

  useEffect(() => {
    fetchEcoModes()
    fetchTimelocks()
  }, [chainId])

  let timelockPeriod = null
  let endDate = null
  if (timelocks) {
    const filtered = timelocks.filter((t) => !deniedTasks?.includes(t?.task))
    filtered.sort((a, b) => new Date(a?.end).getTime() - new Date(b?.end).getTime())
    const closest = filtered[0]
    const startDate = moment(closest?.start)
    endDate = moment(closest?.end)
    timelockPeriod = moment.duration(endDate?.diff(startDate))?.asMilliseconds()
  }

  const handleDeleteEcoMode = (item: EcoMode) => {
    setDeleteEcoMode(item)
    setEcoModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (deleteEcoMode) {
      try {
        await callApi<void>(`/relayer-executor/eco-modes/${deleteEcoMode._id}`, {}, 'DELETE')
        fetchEcoModes()
      } catch (error) {}
    }
    setEcoModalOpen(false)
  }

  const handleEdit = (item: EcoMode, index: number) => {
    setEditIndex(index)
    setEditItem(item)
    setEditedProps({ ...item })
  }

  const handleCancelEdit = () => {
    setEditIndex(null)
    setEditedProps(null)
  }

  const handleSaveEdit = async () => {
    try {
      await callApi<void>(`/relayer-executor/eco-modes/${editItem?._id}`, { ...editedProps }, 'PUT')
      fetchEcoModes()
    } catch (error) {
      console.error('Error saving changes:', error)
    }
    setEditIndex(null)
  }

  const canAdd = smartVaults?.filter((sv: any) => sv.chainId === chainId).length > (ecoModes?.length || 0)

  return (
    <Wrap>
      <Header>
        <SectionTitle>Eco Modes</SectionTitle>
        {!form && canAdd && (
          <AddBtn onClick={() => setForm(true)}>+ Add</AddBtn>
        )}
        {form && (
          <GhostBtn onClick={() => setForm(false)}>Hide form</GhostBtn>
        )}
      </Header>

      {form && <EcoModeForm chainId={chainId} smartVaults={smartVaults} onSuccess={fetchEcoModes} />}

      {ecoModes?.length > 0 && (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>Smart vault</Th>
                <Th>Active</Th>
                <Th>Smooth factor</Th>
                <Th>Safe guard pct</Th>
                <Th>Max exec / period</Th>
                <Th>Gas median threshold</Th>
                <Th></Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {ecoModes.map((item, index) => {
                const isEditing = editIndex === index
                return (
                  <tr key={index}>
                    <Td><Mono>{item.smartVault}</Mono></Td>
                    <Td>
                      {isEditing ? (
                        <MiniSwitch
                          ison={editedProps?.active}
                          onToggle={() => setEditedProps({ ...editedProps, active: !editedProps?.active })}
                        />
                      ) : (
                        <StatusDot ok={item.active} title={item.active ? 'Active' : 'Inactive'} />
                      )}
                    </Td>
                    <Td>
                      {isEditing ? (
                        <InlineInput
                          type="number"
                          value={editedProps?.averageSuccessSpeedSmoothFactor}
                          onChange={(e) => setEditedProps({ ...editedProps, averageSuccessSpeedSmoothFactor: parseFloat(e.target.value) })}
                        />
                      ) : item.averageSuccessSpeedSmoothFactor}
                    </Td>
                    <Td>
                      {isEditing ? (
                        <InlineInput
                          type="number"
                          value={editedProps?.safeGuardPeriodPct}
                          onChange={(e) => setEditedProps({ ...editedProps, safeGuardPeriodPct: parseFloat(e.target.value) })}
                        />
                      ) : (
                        <>
                          {item.safeGuardPeriodPct}
                          {endDate && timelockPeriod && (
                            <Hint>
                              {endDate.clone().subtract(timelockPeriod * item.safeGuardPeriodPct, 'milliseconds').utc().format('MM-DD HH:mm')}
                            </Hint>
                          )}
                        </>
                      )}
                    </Td>
                    <Td>
                      {isEditing ? (
                        <InlineInput
                          type="number"
                          value={editedProps?.maximumExecutionsPerPeriod}
                          onChange={(e) => setEditedProps({ ...editedProps, maximumExecutionsPerPeriod: parseFloat(e.target.value) })}
                        />
                      ) : item.maximumExecutionsPerPeriod}
                    </Td>
                    <Td>
                      {isEditing ? (
                        <InlineInput
                          type="number"
                          value={editedProps?.gasPriceMedianThreshold}
                          onChange={(e) => setEditedProps({ ...editedProps, gasPriceMedianThreshold: parseFloat(e.target.value) })}
                        />
                      ) : item.gasPriceMedianThreshold}
                    </Td>
                    <Td>
                      {isEditing ? (
                        <BtnGroup>
                          <SaveBtn onClick={handleSaveEdit}>Save</SaveBtn>
                          <GhostBtn onClick={handleCancelEdit}>Cancel</GhostBtn>
                        </BtnGroup>
                      ) : (
                        <EditBtn onClick={() => handleEdit(item, index)}>Edit</EditBtn>
                      )}
                    </Td>
                    <Td>
                      <DeleteBtn onClick={() => handleDeleteEcoMode(item)}>Delete</DeleteBtn>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {ecoModes?.length === 0 && (
        <EmptyState>No eco modes configured for this environment</EmptyState>
      )}

      {ecoModalOpen && (
        <CustomConfirmationModal
          message="Are you sure you want to delete this eco mode?"
          onConfirm={handleConfirmDelete}
          onCancel={() => setEcoModalOpen(false)}
        />
      )}
    </Wrap>
  )
}

export default EcoModes

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const SectionTitle = styled.h2`
  color: #777;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin: 0;
`

const AddBtn = styled.button`
  height: 28px;
  padding: 0 12px;
  background: rgba(111,92,230,0.1);
  border: 1px solid rgba(111,92,230,0.3);
  border-radius: 5px;
  color: #a89aff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  &:hover { background: rgba(111,92,230,0.2); border-color: rgba(111,92,230,0.5); }
`

const GhostBtn = styled.button`
  height: 28px;
  padding: 0 10px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 5px;
  color: #777;
  font-size: 12px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: rgba(255,255,255,0.15); color: #a0a0a0; }
`

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const Th = styled.th`
  padding: 10px 12px;
  text-align: left;
  color: #a1a1a1;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`

const Td = styled.td`
  padding: 10px 12px;
  color: #a0a0a0;
  font-size: 13px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  tr:last-child & { border-bottom: none; }
`

const Mono = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 11px;
  color: #777;
`

const Hint = styled.span`
  display: block;
  font-size: 10px;
  color: #777;
  font-family: 'SF Mono', 'Fira Code', monospace;
  margin-top: 2px;
`

const StatusDot = styled.span<{ ok: boolean }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ ok }) => ok ? '#33C2B0' : '#ff4444'};
`

const InlineInput = styled.input`
  background: #0d0d0d;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 4px;
  color: #ededed;
  font-size: 12px;
  padding: 0 8px;
  height: 26px;
  width: 80px;
  outline: none;
  &:focus { border-color: rgba(111,92,230,0.5); }
`

const BtnGroup = styled.div`
  display: flex;
  gap: 4px;
`

const EditBtn = styled.button`
  height: 26px; padding: 0 9px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 4px; color: #a0a0a0; font-size: 11px; cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: rgba(111,92,230,0.4); color: #a89aff; }
`

const SaveBtn = styled(EditBtn)`
  &:hover { border-color: rgba(51,194,176,0.4); color: #33C2B0; }
`

const DeleteBtn = styled(EditBtn)`
  &:hover { border-color: rgba(255,68,68,0.4); color: #ff4444; }
`

const EmptyState = styled.p`
  color: #777;
  font-size: 13px;
  padding: 16px 0;
`
